import React, { useEffect, useState } from 'react';
import debounce from 'lodash.debounce'; 
import styled from 'styled-components';
import useLayoutStore from '../stores/useLayoutStore';
import { useMediaQuery } from '@mantine/hooks';

// Styled components
const LayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  overflow-x: hidden; 
  
`;

const LayoutRow = styled.div`
  display: grid;
  grid-template-columns: ${(props) =>
    props.isToggle
      ? '240px 60px 32px repeat(12, minmax(0, 1fr)) 240px'
      : '240px 240px 32px repeat(12, minmax(0, 1fr)) 240px'};
  grid-gap: 0px;
  max-width: 1920px;
  margin: 0 auto;
  width: 100%;
  padding-top: 70px;

  @media (max-width: 1600px) {
      grid-template-columns: ${(props) =>
    props.isToggle
      ? '112px 60px 32px repeat(12, minmax(0, 1fr)) 112px'
      : '112px 240px 32px repeat(12, minmax(0, 1fr)) 112px'};
    max-width: 1600px;
  }

  @media (max-width: 1440px) {
    grid-template-columns: ${(props) =>
    props.isToggle
      ? '32px 60px 32px repeat(12, minmax(0, 1fr)) 32px'
      : '32px 240px 32px repeat(12, minmax(0, 1fr)) 32px'};
    max-width: 1440px;
  }

  @media (max-width: 1280px) {
    grid-template-columns: ${(props) =>
    props.isToggle
      ? '12px 60px 32px repeat(12, minmax(0, 1fr)) 12px'
      : '12px 238px 32px repeat(12, minmax(0, 1fr)) 12px'};
    max-width: 1280px;
  }

  @media (max-width: 1024px) {
    grid-template-columns: ${(props) =>
    props.isToggle
      ? '10px 60px 32px repeat(12, minmax(0, 1fr)) 10px'
      : '10px 183px 32px repeat(12, minmax(0, 1fr)) 10px'};
    max-width: 1020px;
  }

  @media (max-width: 834px) {
    display: block; /* Drop the grid on small screens */
    max-width: 100%;
  }
`;

const SidebarLarge = styled.aside`
  grid-column: 2;
  text-align: center;
  position: relative;
  height: 100%;
  z-index: 15;

  @media (max-width: 834px) {
    display: none;
  }
`;

const SidebarSmall = styled.aside`
  display: none;
  position: fixed;
  top: 60px;
  left: 0;
  height: calc(100vh - 60px);
  width: 260px;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  transform: translateX(-100%);
  transition: transform 0.3s ease-in-out;
  z-index: 1100;

  &.active {
    transform: translateX(0);
  }

  @media (max-width: 834px) {
    display: block;
  }
`;

const SidebarCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  padding: 10px;
  cursor: pointer;
  color: #111827;
  position: absolute;
  top: 4px;
  right: 6px;
`;

const Content = styled.main`
  grid-column: 4 / span 12;

  @media (max-width: 834px) {
    grid-column: 2 / span 12;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 60px;
  left: 0;
  width: 100vw;
  height: calc(100vh - 60px);
  z-index: 1000;
  padding-left: 5px;
  padding-right: 5px;
  
`;

const Layout = ({ header, sidebarContent, mainContent }) => {
  const { isToggle } = useLayoutStore();
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const isMobile = useMediaQuery('(max-width: 834px)');

  const handleMenuClick = () => {
    setIsSidebarVisible(!isSidebarVisible); // Toggle sidebar visibility for small screens
  };

  useEffect(() => {
    const debouncedHandleResize = debounce(() => window.innerWidth > 834 && setIsSidebarVisible(false), 200);
    window.addEventListener('resize', debouncedHandleResize);
    return () => window.removeEventListener('resize', debouncedHandleResize);
  }, []);

  return (
    <LayoutContainer>
      {/* Pass handleMenuClick to the header so it can trigger sidebar toggle */}
      {React.cloneElement(header || <></>, { onMenuClick: handleMenuClick })}

      {isMobile && isSidebarVisible && <Overlay onClick={handleMenuClick} />}

      <LayoutRow isToggle={isToggle}>
        <SidebarLarge>
          {sidebarContent}
        </SidebarLarge>
        <Content>
          {mainContent}
        </Content>
      </LayoutRow>

      <SidebarSmall className={isSidebarVisible ? 'active' : ''}>
        <SidebarCloseButton onClick={handleMenuClick}>Close</SidebarCloseButton>
        {sidebarContent}
      </SidebarSmall>
    </LayoutContainer>
  );
};

export default Layout;
